import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { api, type S } from "@/shared/api/client";
import { money, num } from "@/shared/lib/format";
import { useAction } from "@/shared/lib/query";
import { useCan } from "@/shared/lib/session";
import { Badge } from "@/shared/ui/Badge";
import { DataTable } from "@/shared/ui/DataTable";
import { Drawer } from "@/shared/ui/Modal";
import { Money } from "@/shared/ui/Money";

export type Card = S["HrCard"];
type CardKPI = S["HrCardKPI"];


/** An indicator's name in the reader's language. */
export function kpiName(k: { name_ar: string; name_en?: string }, lang: string) {
  return lang === "en" && k.name_en ? k.name_en : k.name_ar;
}

/** A value in its unit: money, a count, a percentage, hours, days or a rating. */
export function KPIValue({ v, unit, currency }: { v?: string | null; unit: string; currency: string }) {
  const { t } = useTranslation();
  if (v == null) return <span className="text-ink-3">—</span>;
  if (unit === "amount") return <Money value={v} currency={currency} />;
  return <span className="num whitespace-nowrap">{unit === "percent" ? `${num(v, 1)}%` : unit === "hours" ? t("kpi.hoursN", { n: num(v, 1) })
    : unit === "days" ? t("kpi.daysN", { n: num(v, 1) }) : num(v, 1)}</span>;
}

/** A score against its threshold: green once it earns the bonus. */
export function ScoreBadge({ score, threshold }: { score?: string | null; threshold: number }) {
  if (score == null) return <span className="text-ink-3">—</span>;
  return <Badge tone={Number(score) >= threshold ? "green" : "gold"}><span className="num">{num(score, 1)}%</span></Badge>;
}

/** The month's cards: each person's score and bonus; a row opens the card. */
export function KPICardsTable({ month, app, scorecardId, onOpen }: { month: string; app?: string; scorecardId?: string; onOpen: (c: Card) => void }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["/v1/hr/kpi-cards", month, app ?? "", scorecardId ?? ""],
    queryFn: () => api.get<Card[]>("/v1/hr/kpi-cards", { month, ...(app ? { app } : {}), ...(scorecardId ? { scorecard_id: scorecardId } : {}) }) });
  return (
    <DataTable rows={q.data ?? []} loading={q.isLoading} error={q.error} rowKey={(c) => c.employee_id} onRowClick={onOpen} empty={t("kpi.noCards")}
      columns={[
        { key: "n", header: t("common.name"), cell: (c) => <span><span className="font-medium">{c.name}</span>
          {c.department && <span className="block text-[12px] text-ink-3">{c.department}</span>}</span> },
        { key: "c", header: t("kpi.card"), hideOnMobile: true, cell: (c) => c.scorecard },
        { key: "s", header: t("kpi.score"), align: "end", cell: (c) => <span className="inline-flex items-center gap-1.5">
          {c.missing > 0 && <Badge tone="neutral">{t("kpi.missingN", { count: c.missing })}</Badge>}
          <ScoreBadge score={c.score_pct} threshold={c.threshold_pct} /></span> },
        { key: "b", header: t("kpi.bonus"), align: "end", cell: (c) => c.target_bonus ? <span>
          <Money value={c.bonus} currency={c.currency} strong />
          <span className="block text-[12px] text-ink-3">{c.paid ? t("kpi.paid") : t("kpi.ofN", { v: money(c.target_bonus, c.currency) })}</span></span>
          : <span className="text-ink-3">—</span> },
      ]} />
  );
}

/** One person's month: each indicator's target, result and share of it; HR changes targets and enters manual results. */
export function KPICardDrawer({ card, onClose }: { card: Card | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const can = useCan();
  const edit = !!card && !card.paid && can("hr.settings.update");
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Record<string, { value: string; note: string }>>({});
  useEffect(() => {
    if (!card) return;
    setTargets(Object.fromEntries((card.kpis ?? []).map((k) => [k.kpi_id, k.target])));
    setResults(Object.fromEntries((card.kpis ?? []).filter((k) => k.manual).map((k) => [k.kpi_id, { value: k.actual ?? "", note: k.note ?? "" }])));
  }, [card]);
  const save = useAction(() => {
    const kpis = card!.kpis ?? [];
    return api.put(`/v1/hr/kpi-cards/${card!.employee_id}/${card!.month}`, {
      targets: kpis.filter((k) => targets[k.kpi_id] !== k.target).map((k) => ({ kpi_id: k.kpi_id, target: targets[k.kpi_id] || undefined })),
      results: kpis.filter((k) => k.manual).map((k) => ({ kpi_id: k.kpi_id, value: results[k.kpi_id]?.value || undefined, note: results[k.kpi_id]?.note ?? "" })),
    });
  }, { invalidate: [["/v1/hr/kpi-cards"]], success: t("common.saved"), onSuccess: onClose });
  const dec = (s: string) => s.replace(/[^\d.]/g, "");
  return (
    <Drawer open={!!card} onClose={onClose} title={card ? `${card.name} · ${card.scorecard}` : ""}
      actions={edit && <button className="btn-primary py-1" disabled={save.isPending} onClick={() => save.mutate(undefined)}>{t("common.save")}</button>}>
      {card && (
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-[var(--radius-ctl)] border border-line p-3"><div className="text-[12px] text-ink-3">{t("kpi.score")}</div>
              <div className="mt-1"><ScoreBadge score={card.score_pct} threshold={card.threshold_pct} /></div></div>
            <div className="rounded-[var(--radius-ctl)] border border-line p-3"><div className="text-[12px] text-ink-3">{t("kpi.payout")}</div>
              <div className="num mt-1 font-semibold">{num(card.payout_pct, 1)}%</div></div>
            <div className="rounded-[var(--radius-ctl)] border border-line p-3"><div className="text-[12px] text-ink-3">{card.paid ? t("kpi.paid") : t("kpi.bonus")}</div>
              <Money value={card.bonus} currency={card.currency} strong /></div>
          </div>
          <p className="text-[12.5px] text-ink-2">{t("kpi.cardRule", { threshold: card.threshold_pct })}</p>
          <ul className="divide-y divide-line-2 rounded-[var(--radius-ctl)] border border-line">
            {(card.kpis ?? []).map((k: CardKPI) => {
              const a = k.achievement_pct == null ? null : Number(k.achievement_pct);
              return (
                <li key={k.kpi_id} className="space-y-2 p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{kpiName(k, i18n.language)}{k.manual && <span className="ms-1.5 text-[12px] text-ink-3">{t("kpi.manual")}</span>}</span>
                    <span className="num text-[12px] text-ink-3">{t("kpi.weightN", { n: k.weight })}</span>
                  </div>
                  <div className="grid grid-cols-3 items-end gap-3 text-[13px]">
                    <div><div className="text-[12px] text-ink-3">{t(k.direction === "lower" ? "kpi.targetAtMost" : "kpi.target")}</div>
                      {edit ? <input className={clsx("field num py-1", k.own_target && "border-saffron")} dir="ltr" inputMode="decimal" aria-label={`${t("kpi.target")} ${kpiName(k, i18n.language)}`}
                        value={targets[k.kpi_id] ?? ""} onChange={(e) => setTargets({ ...targets, [k.kpi_id]: dec(e.target.value) })} />
                        : <KPIValue v={k.target} unit={k.unit} currency={card.currency} />}</div>
                    <div><div className="text-[12px] text-ink-3">{t("kpi.actual")}</div>
                      {edit && k.manual ? <input className="field num py-1" dir="ltr" inputMode="decimal" aria-label={`${t("kpi.actual")} ${kpiName(k, i18n.language)}`}
                        value={results[k.kpi_id]?.value ?? ""} onChange={(e) => setResults({ ...results, [k.kpi_id]: { note: results[k.kpi_id]?.note ?? "", value: dec(e.target.value) } })} />
                        : <KPIValue v={k.actual} unit={k.unit} currency={card.currency} />}
                      {k.projected && <div className="text-[11.5px] text-ink-3">{t("kpi.pace")} <KPIValue v={k.projected} unit={k.unit} currency={card.currency} /></div>}</div>
                    <div className="text-end"><div className="text-[12px] text-ink-3">{t("kpi.achieved")}</div>
                      <span className="num font-semibold">{a == null ? "—" : `${num(a, 1)}%`}</span></div>
                  </div>
                  {a != null && <div className="h-1.5 rounded-full bg-line-2"><div className={clsx("h-full rounded-full", a >= 100 ? "bg-ledger" : "bg-saffron")} style={{ width: `${Math.min(100, a)}%` }} /></div>}
                  {edit && k.manual && <input className="field py-1 text-[13px]" placeholder={t("kpi.note")} aria-label={`${t("kpi.note")} ${kpiName(k, i18n.language)}`}
                    value={results[k.kpi_id]?.note ?? ""} onChange={(e) => setResults({ ...results, [k.kpi_id]: { value: results[k.kpi_id]?.value ?? "", note: e.target.value } })} />}
                  {!edit && k.note && <p className="text-[12.5px] text-ink-2">{k.note}</p>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Drawer>
  );
}

